import { useEffect, useState } from "react";
import { X, Download, Bell, Share, Smartphone } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const KEY = "nf_pwa_dismissed";
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const recentlyDismissed = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < 864e5;

let deferred = null;
const subs = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; subs.forEach((f) => f(e)); });
  window.addEventListener("appinstalled", () => { deferred = null; subs.forEach((f) => f(null)); });
}
const OPEN_EVT = "nf-open-install";
export const openInstall = () => window.dispatchEvent(new Event(OPEN_EVT));

function useDeferred() {
  const [evt, setEvt] = useState(deferred);
  useEffect(() => { subs.add(setEvt); return () => { subs.delete(setEvt); }; }, []);
  return evt;
}

export function InstallButton({ className = "", testid = "install-app-btn" }) {
  const { t } = useI18n();
  if (typeof window !== "undefined" && isStandalone()) return null;
  return <button type="button" onClick={openInstall} className={`inline-flex items-center gap-2 ${className}`} data-testid={testid}><Smartphone className="h-4 w-4" />{t("pwa.get")}</button>;
}

export function InstallPrompt() {
  const { t } = useI18n();
  const evt = useDeferred();
  const [open, setOpen] = useState(false);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => {});
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVT, show);
    const timer = !isStandalone() && !recentlyDismissed() ? setTimeout(show, 2500) : null;
    return () => { window.removeEventListener(OPEN_EVT, show); clearTimeout(timer); };
  }, []);

  const close = () => { localStorage.setItem(KEY, String(Date.now())); setOpen(false); };
  const install = async () => {
    evt.prompt();
    const choice = await evt.userChoice;
    deferred = null;
    if (choice.outcome === "accepted") close();
  };
  const notify = async () => {
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted") {
      const reg = await navigator.serviceWorker?.ready;
      reg?.showNotification("NicaFix", { body: t("pwa.notify_hello"), icon: "/img/icon-192.png" });
    }
  };

  if (!open) return null;
  const ios = isIOS();
  return (
    <div className="fixed inset-x-3 bottom-24 sm:inset-x-auto sm:left-6 sm:bottom-24 sm:w-[400px] z-[60] animate-in slide-in-from-bottom-4 fade-in duration-300" data-testid="pwa-install-popup">
      <div className="relative rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 p-5">
        <button onClick={close} className="absolute top-3 end-3 p-1.5 rounded-full hover:bg-slate-100" aria-label="close" data-testid="pwa-close-btn"><X className="h-4 w-4" /></button>
        <div className="flex gap-4">
          <img src="/img/icon-192.png" alt="NicaFix app icon" className="h-14 w-14 rounded-xl ring-1 ring-slate-200 shrink-0" />
          <div className="pe-5">
            <p className="font-semibold text-slate-900" data-testid="pwa-title">{t("pwa.title")}</p>
            <p className="text-sm text-slate-600 mt-1">{t("pwa.body")}</p>
          </div>
        </div>
        {evt && !ios && (
          <button onClick={install} className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-semibold transition-colors" data-testid="pwa-install-btn"><Download className="h-5 w-5" />{t("pwa.install")}</button>
        )}
        <div className="mt-3 space-y-2 text-sm text-slate-700">
          {(ios || !evt) && <p className="flex gap-2 items-start bg-blue-50 rounded-lg p-3" data-testid="pwa-ios-hint"><Share className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />{t("pwa.ios_steps")}</p>}
          {!ios && <p className="flex gap-2 items-start bg-slate-50 rounded-lg p-3" data-testid="pwa-android-hint"><Smartphone className="h-4 w-4 mt-0.5 text-blue-600 shrink-0" />{t("pwa.android")}</p>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {perm === "default" && <button onClick={notify} className="inline-flex items-center gap-2 rounded-full border border-blue-600 text-blue-700 hover:bg-blue-50 px-4 py-2 text-sm font-medium transition-colors" data-testid="pwa-notify-btn"><Bell className="h-4 w-4" />{t("pwa.notify")}</button>}
          {perm === "granted" && <span className="inline-flex items-center gap-2 text-sm text-blue-700 px-2 py-2" data-testid="pwa-notify-on"><Bell className="h-4 w-4" />{t("pwa.notify_on")}</span>}
          <button onClick={close} className="rounded-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 transition-colors" data-testid="pwa-later-btn">{t("pwa.later")}</button>
        </div>
      </div>
    </div>
  );
}
